'use client'

import {cva, type VariantProps} from 'class-variance-authority'
import {Dialog as SheetPrimitive} from '@base-ui/react/dialog'
import {Button} from '@components/ui/button'
import {XCloseIcon} from '@components/ui/icon'
import {cn} from '@utilities/helpers'
import {ModalCloseButton} from './modal-atoms'

const sheetContentVariants = cva(
    'fixed flex flex-col overflow-hidden bg-white shadow-lg transition-transform duration-500 ease-in-out',
    {
        variants: {
            side: {
                bottom: 'inset-x-0 bottom-0 h-auto max-h-[calc(100%-(--spacing(5)))] w-full rounded-t-xl translate-y-0 data-ending-style:translate-y-full data-starting-style:translate-y-full',
                top: 'inset-x-0 top-0 h-auto max-h-[calc(100%-(--spacing(5)))] w-full rounded-b-xl translate-y-0 data-ending-style:-translate-y-full data-starting-style:-translate-y-full',
                right: 'inset-y-0 right-0 h-full w-3/4 translate-x-0 sm:max-w-sm data-ending-style:translate-x-full data-starting-style:translate-x-full',
                left: 'inset-y-0 left-0 h-full w-3/4 translate-x-0 sm:max-w-sm data-ending-style:-translate-x-full data-starting-style:-translate-x-full'
            }
        },
        defaultVariants: {
            side: 'bottom'
        }
    }
)

function Sheet({...props}: SheetPrimitive.Root.Props) {
    return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({...props}: SheetPrimitive.Trigger.Props) {
    return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({...props}: SheetPrimitive.Close.Props) {
    return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({...props}: SheetPrimitive.Portal.Props) {
    return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({className, forceRender, ...props}: SheetPrimitive.Backdrop.Props) {
    return (
        <SheetPrimitive.Backdrop
            className={cn(
                'fixed inset-0 bg-neutral-600 opacity-40 transition-opacity duration-500 ease-in-out data-ending-style:opacity-0 data-starting-style:opacity-0',
                !forceRender && 'z-modal-overlay',
                className
            )}
            forceRender={forceRender}
            data-slot="sheet-overlay"
            {...props}
        />
    )
}

function SheetContent({
    className,
    overlayClassName,
    children,
    showCloseButton = true,
    side = 'bottom',
    forceRenderOverlay = false,
    ...props
}: SheetPrimitive.Popup.Props &
    VariantProps<typeof sheetContentVariants> & {
        overlayClassName?: string
        showCloseButton?: boolean
        forceRenderOverlay?: boolean
    }) {
    return (
        <SheetPortal>
            <SheetOverlay
                className={cn(
                    // This is to ensure the overlay is always on top of the dialog
                    forceRenderOverlay ? 'z-[calc(var(--z-index-modal)+1)]' : 'z-modal-overlay',
                    overlayClassName
                )}
                forceRender={forceRenderOverlay}
            />
            <SheetPrimitive.Popup
                className={cn(
                    sheetContentVariants({side}),
                    forceRenderOverlay ? 'z-[calc(var(--z-index-modal)+2)]' : 'z-modal',
                    className
                )}
                data-slot="sheet-content"
                {...props}
            >
                {children}
                {showCloseButton && (
                    <SheetClose
                        data-slot="sheet-close"
                        render={<ModalCloseButton className="absolute left-2 top-2" />}
                    />
                )}
            </SheetPrimitive.Popup>
        </SheetPortal>
    )
}

function SheetXCloseButton({...props}: SheetPrimitive.Close.Props) {
    return (
        <SheetPrimitive.Close
            data-slot="sheet-close"
            render={
                <Button variant="ghost" size="sm" shape="square">
                    <XCloseIcon />
                </Button>
            }
            {...props}
        />
    )
}

export {Sheet, SheetTrigger, SheetClose, SheetContent, SheetXCloseButton}
